import { TableContainer, Table, Tbody, Th, Thead, Tr, Td, Button, Tooltip, ButtonGroup, Flex, Text, useColorModeValue, HStack, Box, Input, Select, InputGroup, InputLeftAddon, Skeleton } from "@chakra-ui/react"
import { CaretDown, CaretUp, CaretUpDown, Eye, MagnifyingGlass, Pencil, Trash } from "@phosphor-icons/react"
import LoadingRows from "components/AsyncTable/LoadingRows"
import Paginator from "components/AsyncTable/Paginator"
import { Roles } from "enums/roles"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { State } from "state/reducers"

interface Props {
    isLoading: boolean,
    header: Header[],
    footer?: string[],
    data: RowData[] | any[],
    current_page: number,
    per_page: number,
    last_page?: number,
    total: number,
    onPaginationChange: (args1: number, args2: number, args3?: string) => void,
    onDelete?: (args1: string) => void
    linkRow?: boolean
    customActions?: Action[]
    noActions?: boolean
    noView?: boolean
    noEdit?: boolean
    noDelete?: boolean
    canOrderBy?: boolean
}

interface Header {
    label: string,
    acessor: string,
    custom?: Function
}

interface Action {
    label: string,
    callback: (arg1: string) => void,
    icon: React.ReactNode,
    color?: string,
    roles?: Roles[]
}

interface RowData {
    id: string,
}

interface OrderBy {
    field?: string
    direction?: string
}

const AsyncTable = ({
    isLoading,
    header,
    footer,
    data,
    current_page = 1,
    per_page = 30,
    total = 0,
    onPaginationChange,
    onDelete,
    linkRow = false,
    customActions = [],
    noActions = false,
    noView = false,
    noEdit = false,
    noDelete = false,
    canOrderBy = false
}: Props) => {
    const navigate = useNavigate()

    const auth = useSelector(({ auth }: State) => auth)
    const userRole = auth.role.name == "Super Administrador" ? Roles.SUPER_ADMIN : Roles.ADMINISTRADOR ;

    const [orderBy, setOrderBy] = useState<OrderBy>({})
    const [searchValue, setSearchValue] = useState<string>('');

    const [actionHover, setActionHover] = useState<boolean>(false);

    useEffect(() => {
        if (searchValue.length >= 4) {
            const timerId = setTimeout(() => {
                handleSearch(searchValue);
            }, 1000);

            return () => {
                clearTimeout(timerId);
            };
        } else if (searchValue.length == 0) {
            handleSearch("");
        }
    }, [searchValue]);

    const handlePerPage = (value: number) => {
        onPaginationChange(1, value, searchValue);
    }

    const handlePage = (value: number) => {
        onPaginationChange(value, per_page, searchValue);
    }

    const handleChange = (event: any) => {
        setSearchValue(event.target.value);
    };

    const handleSearch = (value: string) => {
        onPaginationChange(1, per_page, value)
    };

    const toDirection = (field: string): string => {
        if (!orderBy.field) return 'desc';
        else if (orderBy.field !== field) return 'desc';
        else if (orderBy.direction === 'desc') return 'asc';
        else return 'desc';
    }

    const handleOrderBy = (field: string) => {
        if (canOrderBy) {
            if (orderBy.direction === 'asc' && orderBy.field === field) {
                setOrderBy({
                })
            } else {
                setOrderBy({
                    field,
                    direction: toDirection(field)
                })
            }
        }
    }

    const rowHoverColor = useColorModeValue('gray.200', 'gray.600')

    return (
        <TableContainer w="full">
            <Box w="full">
                <Flex justifyContent="space-between" alignItems="center" w="100%" minH="80px" px="4">
                    <InputGroup w="250px">
                        <InputLeftAddon><MagnifyingGlass /></InputLeftAddon>
                        <Input
                            placeholder='Pesquisar...'
                            value={searchValue}
                            onChange={handleChange} />
                    </InputGroup>
                    <HStack>
                        <Text fontSize="sm">Linhas por página</Text>
                        {
                            isLoading ?
                                <Skeleton w="80px" h="40px" />
                                :
                                <Select w="80px" value={per_page} onChange={({ target }) => handlePerPage(Number(target.value))}>
                                    <option>10</option>
                                    <option>15</option>
                                    <option>20</option>
                                    <option>25</option>
                                    <option>30</option>
                                    <option>40</option>
                                    <option>50</option>
                                </Select>
                        }
                    </HStack>
                </Flex>
            </Box>
            <Table variant='simple'>
                <Thead>
                    <Tr>
                        {
                            header.map((hd, i) => <Th key={`${hd}_${i}`} cursor={canOrderBy ? "pointer" : "initial"} onClick={() => handleOrderBy(hd.label)}>
                                <HStack>
                                    <Text fontSize="xs">{hd.label}</Text>
                                    {
                                        orderBy.field === hd.label ?
                                            <Box>
                                                {orderBy.direction === 'desc' ? <CaretDown size="18px" /> : <CaretUp size="18px" />}
                                            </Box>
                                            : canOrderBy &&
                                            <Box>
                                                <CaretUpDown size="18px" />
                                            </Box>
                                    }
                                </HStack>
                            </Th>)
                        }
                        {
                            !isLoading && data.length > 0 && !noActions &&
                            <Th
                                display="flex"
                                justifyContent="flex-end"
                            >
                                Ações
                            </Th>
                        }
                    </Tr>
                </Thead>
                <Tbody>
                    {
                        isLoading ? <LoadingRows header={header} />
                            :
                            data.length === 0 ?
                                <Tr>
                                    <Td colSpan={header.length}>
                                        <Flex w="full" justifyContent="center" py={8}>
                                            <Text size="md">Não existem dados para serem exibidos.</Text>
                                        </Flex>

                                    </Td>
                                </Tr>
                                :
                                data.map((dt, i) => (
                                    <Tr
                                        key={`${dt}_${i}`}
                                        cursor={linkRow ? 'pointer' : 'initial'}
                                        _hover={{ backgroundColor: linkRow ? rowHoverColor : 'transparent' }}
                                        onClick={actionHover || !linkRow ? () => { } : () => navigate(`./${dt.id}`)}
                                        transition="0.1s all ease"
                                        zIndex={1}
                                    >
                                        {
                                            header.map((hd, i) =>
                                                <Td key={`${hd}_${i}`}>
                                                    {!hd.custom ? dt[hd.acessor] : hd.custom(dt[hd.acessor])}
                                                </Td>)
                                        }
                                        <Td
                                            position="relative"
                                            zIndex={0}
                                            display="flex"
                                            justifyContent="flex-end"
                                            hidden={noActions} 
                                        >
                                            <ButtonGroup size='sm' isAttached zIndex={1000} onMouseEnter={() => setActionHover(true)} onMouseLeave={() => setActionHover(false)}>
                                                {
                                                    !noActions && <>
                                                        {
                                                            !noView &&
                                                            <Tooltip label="Visualizar" hidden={noActions || noView}>
                                                                <Button onClick={() => navigate(`./${dt.id}`)} size="sm" colorScheme='blue'><Eye size="16px" weight="bold" /></Button>
                                                            </Tooltip>
                                                        }
                                                        {
                                                            !noEdit &&
                                                            <Tooltip label="Editar" hidden={noActions || noEdit}>
                                                                <Button onClick={() => navigate(`./${dt.id}/editar`)} size="sm" colorScheme='blue'><Pencil size="16px" weight="bold" /></Button>
                                                            </Tooltip>
                                                        }
                                                        {
                                                            !noDelete &&
                                                            <Tooltip label="Remover" hidden={noActions || noDelete}>
                                                                <Button onClick={() => onDelete && onDelete(dt.id)} size="sm" colorScheme='red'><Trash size="16px" weight="bold" /></Button>
                                                            </Tooltip>
                                                        }
                                                    </>
                                                }
                                                {
                                                    customActions?.filter(action => action?.roles?.length == 0 || action?.roles?.includes(userRole)).map((ac, i) =>
                                                        <Tooltip key={`${ac}_${i}`} label={`${ac.label}`}>
                                                            <Button size="sm" onClick={() => ac.callback(dt.id)} colorScheme={ac?.color || 'blue'} >{ac.icon}</Button>
                                                        </Tooltip>
                                                    )
                                                }
                                            </ButtonGroup>
                                        </Td>
                                    </Tr>
                                ))
                    }
                </Tbody>
            </Table>
            <Box w="full">
                <Flex hidden={isLoading} justifyContent="space-between" alignItems="center" w="100%" minH="80px" px="4">
                    {!isLoading && data.length > 0 && <Text>Exibindo {data.length} de {total} registros.</Text>}
                    {(isLoading || data.length === 0) && <Box w="10px" h="1px" />}
                    <Paginator total={total} perPage={per_page} current={current_page} setCurrentPage={handlePage} />
                </Flex>
            </Box>
        </TableContainer>
    )
}

export default AsyncTable